import { Button, Typography, useTheme } from '@themenu/design-system'
import { type ChangeEvent, type FC, type FormEvent, type ReactNode, useId, useState } from 'react'
import { useIntl } from 'react-intl'

import { FormikFieldInputPassword } from '../../../components/Form/FormikFieldInputPassword'
import {
  hasOneCapitalLetterMin,
  hasOneLowerCaseLetterMin,
  hasOneNumberMin,
  hasOneSpecialCharacterMin,
  hasValidLength,
  passwordAllRules,
} from '../../../utils/passwordValidators'
import { ActivationStepLayout } from './ActivationStepLayout'

/**
 * Step 4 — set the activation password. Mirrors `ChangePasswordView`: the same
 * `Criterion` component, the shared `utils/passwordValidators`, and an
 * `aria-live="polite"` criteria region. There is no "current password" field
 * (the user is setting it for the first time during activation).
 */
const Criterion: FC<{ isValid: boolean; text: ReactNode; character?: 'link' | null }> = ({
  isValid,
  text,
  character = ', ',
}) => {
  const theme = useTheme()
  const { formatMessage } = useIntl()

  const successIcon = isValid && (
    <>
      <span aria-hidden>&#10003;</span>
      <span className="sr-only">{formatMessage({ id: 'change_password.criteria.check' })}</span>
    </>
  )

  const characterContent =
    character === 'link' ? ` ${formatMessage({ id: 'change_password.criteria.character' })}` : character

  return (
    <Typography
      variant="body"
      style={isValid ? { color: theme.colors.content.success } : undefined}
      display="inline"
      component="span"
    >
      {text}
      {successIcon}
      <span>{characterContent}</span>
    </Typography>
  )
}

export type PasswordStepProps = {
  onSubmit: (password: string, passwordConfirmation: string) => Promise<void>
  errorMessageId?: string | null
  showBackButton?: boolean
  onBack?: () => void
}

export const PasswordStep: FC<PasswordStepProps> = ({
  onSubmit,
  errorMessageId = null,
  showBackButton = false,
  onBack,
}) => {
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const mismatchId = useId()

  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const showMismatch = confirmation.length > 0 && confirmation !== password
  const isValid = passwordAllRules(password) && password === confirmation

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    if (!isValid || submitting) {
      return
    }
    setSubmitting(true)
    try {
      await onSubmit(password, confirmation)
    } finally {
      setSubmitting(false)
    }
  }

  const criteria = formatMessage(
    { id: 'change_password.criteria' },
    {
      length: (text: ReactNode) => <Criterion isValid={hasValidLength(password)} text={text} />,
      number: (text: ReactNode) => <Criterion isValid={hasOneNumberMin(password)} text={text} />,
      lowercase: (text: ReactNode) => <Criterion isValid={hasOneLowerCaseLetterMin(password)} text={text} />,
      uppercase: (text: ReactNode) => (
        <Criterion isValid={hasOneCapitalLetterMin(password)} text={text} character="link" />
      ),
      special: (text: ReactNode) => (
        <Criterion isValid={hasOneSpecialCharacterMin(password)} text={text} character={null} />
      ),
    },
  )

  return (
    <ActivationStepLayout
      titleId="br_activation.password.title"
      errorMessageId={errorMessageId}
      showBackButton={showBackButton}
      onBack={onBack}
      onSubmit={handleSubmit}
      testId="br-activation-password-form"
      footer={
        <Button
          data-testid="br-activation-password-submit"
          type="submit"
          variant="primary"
          className="w-full"
          disabled={!isValid || submitting}
          loading={submitting}
        >
          {formatMessage({ id: 'change_password.submit' })}
        </Button>
      }
    >
      <Typography variant="body">{formatMessage({ id: 'br_activation.password.description' })}</Typography>
      <div aria-live="polite" aria-atomic>
        <Typography variant="body" component="p">
          {criteria}
        </Typography>
      </div>
      <FormikFieldInputPassword
        // biome-ignore lint/suspicious/noExplicitAny: standalone use of the shared password field outside Formik
        field={{ name: 'new_password', value: password } as any}
        // biome-ignore lint/suspicious/noExplicitAny: form prop unused in standalone mode
        form={{ touched: {}, errors: {} } as any}
        // biome-ignore lint/suspicious/noExplicitAny: meta prop unused in standalone mode
        meta={{ touched: false, error: undefined } as any}
        innerRef={{ current: null }}
        id="br-activation-new-password"
        data-testid="br-activation-new-password"
        type="password"
        spellCheck="false"
        autoComplete="new-password"
        label={formatMessage({ id: 'change_password.new_password' })}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
      />
      <FormikFieldInputPassword
        // biome-ignore lint/suspicious/noExplicitAny: standalone use of the shared password field outside Formik
        field={{ name: 'confirm_password', value: confirmation } as any}
        // biome-ignore lint/suspicious/noExplicitAny: form prop unused in standalone mode
        form={{ touched: {}, errors: {} } as any}
        // biome-ignore lint/suspicious/noExplicitAny: meta prop unused in standalone mode
        meta={{ touched: false, error: undefined } as any}
        innerRef={{ current: null }}
        id="br-activation-confirm-password"
        data-testid="br-activation-confirm-password"
        type="password"
        spellCheck="false"
        autoComplete="new-password"
        aria-describedby={mismatchId}
        label={formatMessage({ id: 'change_password.confirm_password' })}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setConfirmation(e.target.value)}
      />
      {showMismatch && (
        <Typography
          id={mismatchId}
          variant="body"
          style={{ color: theme.colors.content.critical }}
          data-testid="br-activation-password-mismatch"
        >
          {formatMessage({ id: 'change_password.error.password_mismatch' })}
        </Typography>
      )}
    </ActivationStepLayout>
  )
}
